import { useMemo, useState } from "react";
import { Chip, Select, Toast } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import {
  copyTemplates as copyTemplatesApi,
  type CopyTemplate,
  type EntityScope,
  type Language,
  type RankingBand,
  type TemplateType,
} from "@/api/copyTemplates.service";
import { TemplateEditorPanel } from "./TemplateEditorPanel";
import { ENTITY_SCOPES, LANGUAGES, RANKING_BANDS, TEMPLATE_TYPES } from "./copy-types";
import styles from "./CopyTemplates.module.css";

type TemplateRow = {
  templateType: TemplateType;
  label: string;
  byLanguage: Partial<Record<Language, CopyTemplate>>;
  lastSavedAt: string | null;
};

function lastSavedLabel(row: TemplateRow): string {
  if (!row.lastSavedAt) return "—";
  return new Date(row.lastSavedAt).toLocaleDateString();
}

function CopyTemplatesPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.marketing.read");
  const [entityScope, setEntityScope] = useState<EntityScope>("firm");
  const [rankingBand, setRankingBand] = useState<RankingBand>("any");
  const [language, setLanguage] = useState<Language>("en");
  const [openType, setOpenType] = useState<TemplateType | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const { data, loading, error, refetch } = useQuery(
    () => copyTemplatesApi.list({ entityScope, rankingBand }),
    [entityScope, rankingBand],
  );

  const { data: placeholderData } = useQuery(() => copyTemplatesApi.placeholders(), []);

  const rows = useMemo<TemplateRow[]>(() => {
    const templates = data?.templates ?? [];

    return TEMPLATE_TYPES.map(type => {
      const byLanguage: Partial<Record<Language, CopyTemplate>> = {};
      let lastSavedAt: string | null = null;

      for (const template of templates) {
        if (template.templateType !== type.value) continue;
        byLanguage[template.language] = template;
        if (!lastSavedAt || template.updatedAt > lastSavedAt) lastSavedAt = template.updatedAt;
      }

      return { templateType: type.value, label: type.label, byLanguage, lastSavedAt };
    });
  }, [data]);

  const columns = useMemo<Column<TemplateRow>[]>(
    () => [
      { header: "Copy type", accessor: "label" },
      ...LANGUAGES.map(option => ({
        header: option.label,
        accessor: option.value as keyof TemplateRow,
        render: (_value: TemplateRow[keyof TemplateRow], row: TemplateRow) => {
          const template = row.byLanguage[option.value];
          return (
            <span className={styles.languageCell}>
              <Chip
                size="small"
                variant={template ? "filled" : "outlined"}
                color={template ? "success" : "default"}
              >
                {template ? `Saved · v${template.version}` : "Empty"}
              </Chip>
            </span>
          );
        },
      })),
      {
        header: "Last saved",
        accessor: "lastSavedAt" as keyof TemplateRow,
        render: (_value: TemplateRow[keyof TemplateRow], row: TemplateRow) => lastSavedLabel(row),
      },
    ],
    [],
  );

  const openRow = rows.find(row => row.templateType === openType) ?? null;
  const errorStatus = (error as (Error & { status?: number }) | null)?.status;

  if (!canRead || errorStatus === 401 || errorStatus === 403) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Marketing Copy Templates"
        subtitle="Marketing Toolkit — the copy ranked firms download"
      />

      <div className={styles.filters}>
        <div className={styles.filter}>
          <Select
            label="Written for"
            value={entityScope}
            options={ENTITY_SCOPES}
            onChange={event => {
              setEntityScope(event.target.value as EntityScope);
              setOpenType(null);
            }}
          />
        </div>
        <div className={styles.filter}>
          <Select
            label="Ranking band"
            value={rankingBand}
            options={RANKING_BANDS}
            onChange={event => {
              setRankingBand(event.target.value as RankingBand);
              setOpenType(null);
            }}
          />
        </div>
      </div>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading ? (
        <LoadingState message="Loading copy templates…" />
      ) : (
        <div className={styles.tableSection}>
          <DataTable
            data={rows}
            columns={columns}
            rowKey="templateType"
            onRowClick={row => setOpenType(row.templateType)}
            emptyMessage="No copy types configured."
          />
        </div>
      )}

      {openRow && (
        <TemplateEditorPanel
          templateType={openRow.templateType}
          entityScope={entityScope}
          rankingBand={rankingBand}
          language={language}
          onLanguageChange={setLanguage}
          savedByLanguage={openRow.byLanguage}
          placeholders={placeholderData?.placeholders ?? []}
          onSaved={message => setToast({ message, type: "success" })}
          onError={message => setToast({ message, type: "error" })}
          onClose={() => setOpenType(null)}
          refetchTemplates={refetch}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default CopyTemplatesPage;
